// frontend/src/components/Search/AdvancedSearchBuilder.jsx
// Dynamic condition builder for advanced search (CBETA style)

import { Box, Button, Typography, IconButton, Tooltip } from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, HelpOutline as HelpIcon } from '@mui/icons-material';
import ConditionRow from './ConditionRow';
import { buildAdvancedQuery, createCondition } from '../../utils/queryBuilder';

/**
 * Maximum number of conditions allowed
 */
const MAX_CONDITIONS = 10;

/**
 * AdvancedSearchBuilder - Dynamic condition builder for advanced search
 *
 * @param {Array} conditions - Array of condition objects
 * @param {Function} onConditionsChange - Callback when conditions change
 * @param {Function} onSearch - Callback to execute search
 * @param {boolean} disabled - Whether the builder is disabled
 */
const AdvancedSearchBuilder = ({
  conditions = [],
  onConditionsChange,
  onSearch,
  disabled = false,
}) => {
  /**
   * Add a new condition
   */
  const handleAddCondition = () => {
    if (conditions.length < MAX_CONDITIONS) {
      onConditionsChange([...conditions, createCondition('AND')]);
    }
  };

  /**
   * Update a specific condition
   */
  const handleConditionChange = (index, updatedCondition) => {
    const newConditions = [...conditions];
    newConditions[index] = updatedCondition;
    onConditionsChange(newConditions);
  };

  /**
   * Remove a condition
   */
  const handleRemoveCondition = (index) => {
    if (conditions.length > 1) {
      const newConditions = conditions.filter((_, i) => i !== index);
      onConditionsChange(newConditions);
    }
  };

  /**
   * Handle search submission
   */
  const handleSearch = () => {
    const query = buildAdvancedQuery(conditions);
    if (query) {
      onSearch(query);
    }
  };

  /**
   * Handle Enter key in any input
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // Generate query preview
  const queryPreview = buildAdvancedQuery(conditions);
  const hasValidQuery = queryPreview.length > 0;
  const canAddMore = conditions.length < MAX_CONDITIONS;

  return (
    <Box sx={{ p: 2, width: '100%' }} onKeyPress={handleKeyPress}>
      {/* Condition rows */}
      <Box sx={{ mb: 2 }}>
        {conditions.map((condition, index) => (
          <ConditionRow
            key={condition.id}
            condition={condition}
            onChange={(updated) => handleConditionChange(index, updated)}
            onRemove={() => handleRemoveCondition(index)}
            isFirst={index === 0}
            showRemove={conditions.length > 1}
            disabled={disabled}
          />
        ))}
      </Box>

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Add condition button */}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddCondition}
          disabled={!canAddMore || disabled}
          sx={{
            borderStyle: 'dashed',
          }}
          variant="outlined"
          color="success"
        >
          新增條件
          {!canAddMore && ` (最多 ${MAX_CONDITIONS} 個)`}
        </Button>

        {/* Help tooltip */}
        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>進階搜尋說明：</strong>
              </Typography>
              <Typography variant="caption" component="div">
                - AND：兩個詞必須同時出現
              </Typography>
              <Typography variant="caption" component="div">
                - OR：任一個詞出現即可
              </Typography>
              <Typography variant="caption" component="div">
                - NOT：排除該詞
              </Typography>
            </Box>
          }
        >
          <IconButton size="small">
            <HelpIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Search button */}
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={!hasValidQuery || disabled}
        >
          搜尋
        </Button>
      </Box>

      {/* Query preview */}
      {hasValidQuery && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            backgroundColor: 'grey.100',
            borderRadius: 1,
            borderLeft: '3px solid',
            borderColor: 'primary.main',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            查詢預覽：
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
            {queryPreview}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AdvancedSearchBuilder;
